import React, { FC, PropsWithChildren, ReactNode } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import LockIcon from 'src/shared/material_icons/action/lock.svg';
import styled from 'styled-components';
import { Section } from '../section';
import { ifCanAccessQuery } from './__generated__/ifCanAccessQuery.graphql';
import { Icon } from '../icon';
import { LinkButton } from '../buttons';
import { ContainerBase } from '../layouts';
import Stack from '../stack/stack';

const StyledContainerBase = styled(ContainerBase)<{ $layout: 'page' | 'section' }>`
    ${({ $layout }) =>
        $layout === 'page' &&
        `
            display: flex;
            flex: 1;
            flex-direction: column;
            justify-content: center;
            min-height: calc(100dvh - 48px);
            padding: 24px calc(48px - var(--app-content-gutter, 48px));
            box-sizing: border-box;
        `}
`;

const StyledIcon = styled(Icon)`
    width: 64px;
    height: 64px;
    min-width: 64px;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const StyledContainer = styled(Stack)`
    align-items: center;
    padding: 12px 0;
    text-align: center;

    h3,
    p {
        margin: 0;
    }
`;

const IfCanAccess: FC<PropsWithChildren<{ permission: string; fallback?: ReactNode; layout?: 'page' | 'section' }>> = ({
    permission,
    fallback,
    layout = 'page',
    children
}) => {
    const { user } = useLazyLoadQuery<ifCanAccessQuery>(
        graphql`
            query ifCanAccessQuery {
                user {
                    email
                }
            }
        `,
        {}
    );

    const { isAllowed } = usePermissions();
    if (!isAllowed([permission])) {
        return (
            fallback || (
                <StyledContainerBase $layout={layout}>
                    <Section>
                        <StyledContainer>
                            <StyledIcon>
                                <LockIcon />
                            </StyledIcon>

                            <h3>{T._("You don't have the permissions to access this page", 'Admin')}</h3>
                            <p>{T._('Email the account owner to get access to this page.', 'Admin')}</p>
                            <LinkButton href={'mailto:' + user.email}>{T._('Request access', 'Admin')}</LinkButton>
                        </StyledContainer>
                    </Section>
                </StyledContainerBase>
            )
        );
    }

    return <>{children}</>;
};

export default IfCanAccess;
